import React, { useState, useMemo, useEffect, memo } from "react";
import { Link } from "@inertiajs/react";
import { Twitter, Instagram, Youtube, Rss } from "lucide-react";
import AppLogoRed from "@/components/assets/AppLogoRed";
import { route } from 'ziggy-js';


interface Category {
    id: number;
    name: string;
    slug: string;
    order?: number;
    views?: number;
}

interface CategoryTab {
    id: number | string;
    label: string;
    color: string;
    slug?: string;
    newsCount?: number;
}

interface HomeFooterProps {
    className?: string;
    categories?: Category[];
}

const HomeFooter: React.FC<HomeFooterProps> = ({
    className = "",
    categories = [],
}) => {
    const [email, setEmail] = useState("");
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [subscriptionMessage, setSubscriptionMessage] = useState("");



    // Use real categories from props, with fallback if needed
    const footerCategories = useMemo(() => {
        if (categories && categories.length > 0) {
            // Process real categories from backend
            const categoryTabs: CategoryTab[] = [
                { id: 0, label: "Beranda", color: "bg-gray-600", slug: "", newsCount: 0 },
                ...categories
                    .filter(cat => cat && cat.name && cat.slug) // Filter valid categories
                    .sort((a, b) => {
                        // Sort by order if available, otherwise by name
                        if (a.order !== undefined && b.order !== undefined) {
                            return a.order - b.order;
                        }
                        return a.name.localeCompare(b.name);
                    })
                    // Show ALL categories - no limit
                    .map(cat => ({
                        id: cat.id,
                        label: cat.name,
                        color: "bg-gray-600",
                        slug: cat.slug,
                        newsCount: 0
                    }))
            ];

            return categoryTabs;
        } else {
            // Fallback categories if no data from backend
            const fallbackTabs: CategoryTab[] = [
                { id: 0, label: "Beranda", color: "bg-gray-600", slug: "", newsCount: 0 },
                { id: 1, label: "Politik", color: "bg-red-600", slug: "politik", newsCount: 0 },
                { id: 2, label: "Ekonomi", color: "bg-green-600", slug: "ekonomi", newsCount: 0 },
                { id: 3, label: "Olahraga", color: "bg-orange-600", slug: "olahraga", newsCount: 0 },
                { id: 4, label: "Teknologi", color: "bg-blue-600", slug: "teknologi", newsCount: 0 },
                { id: 5, label: "Hiburan", color: "bg-purple-600", slug: "hiburan", newsCount: 0 },
            ];

            return fallbackTabs;
        }
    }, [categories]);

    const isLoadingCategories = false;

    // Memoized social media links
    const socialLinks = useMemo(() => [
        {
            name: "X (Twitter)",
            icon: Twitter,
            href: "https://x.com/LambeTurahMedia",
            color: "hover:text-black",
        },
        {
            name: "Instagram",
            icon: Instagram,
            href: "https://instagram.com/lambeturah",
            color: "hover:text-pink-500",
        },
        {
            name: "YouTube",
            icon: Youtube,
            href: "https://www.youtube.com/@LambeTurahYT",
            color: "hover:text-red-500",
        },
        {
            name: "RSS",
            icon: Rss,
            href: route('feed.rss'),
            color: "hover:text-orange-500",
        },
    ], []);


    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();

        // Clear previous messages
        setSubscriptionMessage("");

        // Trim email to remove whitespace
        const trimmedEmail = email.trim();

        if (!trimmedEmail) {
            setSubscriptionMessage("Please enter your email address");
            return;
        }

        // Enhanced email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
            setSubscriptionMessage("Please enter a valid email address");
            return;
        }

        // Check for common email domains to provide better UX
        const commonDomains = [
            "gmail.com",
            "yahoo.com",
            "hotmail.com",
            "outlook.com",
        ];
        const domain = trimmedEmail.split("@")[1]?.toLowerCase();

        setIsSubscribing(true);

        try {
            // TODO: Replace with actual API call to subscription endpoint
            // Example: await fetch('/api/subscribe', { method: 'POST', body: JSON.stringify({ email: trimmedEmail }) })

            // Simulate API call with realistic delay
            await new Promise((resolve, reject) => {
                setTimeout(
                    () => {
                        // Simulate occasional failures for testing
                        if (Math.random() > 0.9) {
                            reject(new Error("Network error"));
                        } else {
                            resolve(true);
                        }
                    },
                    1000 + Math.random() * 1000,
                ); // 1-2 second delay
            });

            setSubscriptionMessage(
                "Thank you for subscribing! Check your email for confirmation.",
            );
            setEmail("");

            // Auto-clear success message after 5 seconds
            setTimeout(() => {
                setSubscriptionMessage("");
            }, 5000);
        } catch (error) {
            console.warn("Subscription error:", error);
            setSubscriptionMessage(
                "Subscription failed. Please try again later.",
            );

            // Auto-clear error message after 3 seconds
            setTimeout(() => {
                setSubscriptionMessage("");
            }, 3000);
        } finally {
            setIsSubscribing(false);
        }
    };

    return (
        <footer
            className={`bg-lg-background dark:bg-dr-background border-t border-gray-200 dark:border-gray-700 ${className}`}
        >
            <div className="container mx-auto mt-8 px-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                    {/* Categories Section */}
                    <div className="lg:col-span-2 my-auto">
                        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 lg:items-start flex-wrap">
                            <h2 className="text-2xl font-semibold text-red-600 flex-shrink-0">
                                Kategori
                            </h2>

                            {/* Loading state */}
                            {isLoadingCategories ? (
                                <div className="flex flex-wrap gap-4">
                                    {[...Array(6)].map((_, index) => (
                                        <div
                                            key={`loading-${index}`}
                                            className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
                                            style={{ width: `${60 + Math.random() * 40}px` }}
                                        />
                                    ))}
                                </div>
                            ) : (
                                footerCategories.length > 0 ? (
                                    footerCategories.map((category) => {
                                        // Determine the correct href based on category type
                                        const href = category.slug === "" ? "/" : `/category/${category.slug}`;

                                        return (
                                            <Link
                                                key={`footer-category-${category.id}-${category.slug || "home"}`}
                                                href={href}
                                                className="text-gray-600 dark:text-gray-300 hover:text-red-600 transition-colors duration-200 text-sm lg:text-base pt-1 whitespace-nowrap"
                                                title={`Browse ${category.label} news`}
                                            >
                                                {category.label}
                                            </Link>
                                        );
                                    })
                                ) : (
                                    <span className="text-gray-500">No categories to display</span>
                                )
                            )}
                        </div>

                        {/* Show fallback message if no categories available */}
                        {!isLoadingCategories && footerCategories.length === 0 && (
                            <div className="mt-2">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    No categories available. Please check back later.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Subscribe Section */}
                    <div className="lg:col-span-1">
                        <h3 className="text-xl font-bold text-red-600 mb-4">
                            Subscribe
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">
                            Join gang kita sekarang juga,
                            <br />
                            dan gak yang pertama tahu kalau ada gosip panas
                            mendadak!
                        </p>

                        <form onSubmit={handleSubscribe} className="space-y-3">
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter Your Email"
                                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                                    disabled={isSubscribing}
                                />
                                <button
                                    type="submit"
                                    disabled={isSubscribing}
                                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubscribing
                                        ? "Subscribing..."
                                        : "Subscribe"}
                                </button>
                            </div>

                            {subscriptionMessage && (
                                <p
                                    className={`text-sm ${
                                        subscriptionMessage.includes(
                                            "Thank you",
                                        )
                                            ? "text-green-600"
                                            : "text-red-600"
                                    }`}
                                >
                                    {subscriptionMessage}
                                </p>
                            )}
                        </form>
                    </div>
                </div>

                {/* Bottom Border */}
                <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6 pt-4 pb-12">
                        <Link href="/">
                            {/* <AppLogo size="100%" className="mx-auto" /> */}
                            <AppLogoRed alt="Logo Merah" className="h-8" />
                        </Link>
                        <p className="text-gray-500 text-sm">
                            © {new Date().getFullYear()} Lambeturah | Develop by <a href="http://software.lugu.id" target="_blank" rel="noopener refferer"><strong className="text-lg">Lugu Software</strong></a> All rights
                      reserved.
                        </p>
                        <div className="flex space-x-6">
                            {socialLinks.map((social, index) => {
                                const IconComponent = social.icon;
                                return (
                                    <a
                                        key={`social-${index}-${social.name}`}
                                        href={social.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`text-gray-400 ${social.color} transition-colors duration-200`}
                                        aria-label={social.name}
                                    >
                                        <IconComponent size={20} />
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(HomeFooter, (prevProps, nextProps) => {
    return (
        prevProps.className === nextProps.className &&
        prevProps.categories?.length === nextProps.categories?.length &&
        JSON.stringify(prevProps.categories) === JSON.stringify(nextProps.categories)
    );
});
